import { FC } from 'react';

import { useCompanyBankAccounts } from 'common/hooks/useCompanyBankAccounts';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import { BankAccountRow } from 'components/BankAccountRow';
import ErrorView from 'components/ErrorView';
import Loader from 'components/Loader';
import { StepProps } from 'components/Stepper/types';
import TrackView from 'features/Analytics/TrackView';
import NavigationHeader from 'features/Payments/Pages/Transfers/components/NavigationHeader';
import {
  BankTransferCreationStep,
  useBankTransferContext,
} from 'features/Payments/Pages/Transfers/contexts';
import * as ContentLayout from 'layouts/ContentLayout';

import locales from './locales';

type SelectToBankAccountProps = Pick<
  StepProps<BankTransferCreationStep>,
  'goNext'
> &
  Partial<Pick<StepProps<BankTransferCreationStep>, 'goBack'>>;

export const SelectToBankAccount: FC<SelectToBankAccountProps> = ({
  goBack,
  goNext,
}) => {
  const { bankAccounts, error, loading } = useCompanyBankAccounts();
  const { bankTransfer, setBankTransfer } =
    useBankTransferContext('selectToAccount');

  const selectableBankAccounts = bankAccounts.filter(
    (bankAccount) =>
      bankAccount.bankAccountId !== bankTransfer.fromBankAccountId,
  );

  const handleBankAccountClick = (bankAccountId: string) => {
    setBankTransfer((prev) => ({
      ...prev,
      toBankAccountId: bankAccountId,
    }));
    goNext();
  };

  if (error) {
    return <ErrorView />;
  }

  return (
    <ContentLayout.Root>
      <TrackView pageName="SelectToBankAccount" />
      <ContentLayout.Body>
        <NavigationHeader onBack={goBack} />
        <Heading size="lg">{locales.toAccount}</Heading>
        {loading ? (
          <Loader />
        ) : (
          <ContentLayout.BodyScrollableSection paddingTop={40}>
            <SunshineCard.Group>
              {selectableBankAccounts.map((bankAccount) => (
                <BankAccountRow
                  bankAccount={bankAccount}
                  key={bankAccount.bankAccountId}
                  onClick={() =>
                    handleBankAccountClick(bankAccount.bankAccountId)
                  }
                />
              ))}
            </SunshineCard.Group>
          </ContentLayout.BodyScrollableSection>
        )}
      </ContentLayout.Body>
    </ContentLayout.Root>
  );
};
